import React, { useMemo, useState } from "react";
import ExpandableSearch from "../ExpandableSearch";
import ViewActionButton from "./ViewActionButton";

const DataTable = ({
  title,
  headings,
  data,
  columns,
  onAddNew,
  onView,
  emptyMessage = "No records found.",
  showEmptyState = true,
  tableClassName = "min-w-[640px]",
  columnClassNames = [],
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const filteredData = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return data;

    return data.filter((row) =>
      columns.some((column) =>
        String(row?.[column] ?? "").toLowerCase().includes(query),
      ),
    );
  }, [columns, data, searchTerm]);
  const getStatusClass = (status) => {
    if (status == null) return "text-black";

    switch (String(status).trim().toLowerCase()) {
      case "active":
      case "open":
      case "online":
        return "text-[#56A503]";
      case "pending":
      case "waiting":
        return "text-yellow-500";
      case "inactive":
      case "disabled":
        return "text-gray-500";
      case "failed":
      case "error":
      case "rejected":
        return "text-red-600";
      default:
        return "text-black";
    }
  };

  return (
    <div className="w-full min-w-0 max-w-full font-Nunito">
      <div
        className={`flex lg:px-6 my-4 items-center ${title ? "justify-between" : "justify-end"}`}
      >
        {title ? (
          <p className="text-2xl font-bold leading-none">{title}</p>
        ) : null}
        <div className="flex min-w-0 items-center gap-2">
          <ExpandableSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={`Search ${title || "records"}`}
          />
        {onAddNew && (
          <button
            onClick={onAddNew}
            className="bg-[#1234CD] px-6 py-2 rounded-xl text-white"
          >
            Add new
          </button>
        )}
        </div>
      </div>

      {/* Table Section */}
      <div className="w-full max-w-full overflow-x-auto overscroll-x-contain rounded-xl border border-slate-100 bg-white">
      <table className={`w-full table-fixed lg:px-6 ${tableClassName}`}>
        <thead>
          <tr>
            {headings.map((heading, idx) => (
              <th
                key={idx}
                className={`text-left py-3 px-6 text-base font-semibold text-black ${columnClassNames[idx] || ""}`}
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="bg-white lg:px-6 rounded-xl mb-4">
          {filteredData.length === 0 && showEmptyState ? (
            <tr className="shadow-sm border border-gray-100">
              <td
                colSpan={columns.length + (onView ? 1 : 0)}
                className="py-10 px-6 text-center text-sm text-gray-500"
              >
                {searchTerm ? "No matching records found." : emptyMessage}
              </td>
            </tr>
          ) : filteredData.length > 0 ? (
            filteredData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="hover:bg-gray-50 shadow-sm border border-gray-100"
              >
                {columns.map((col, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={`py-4 px-6 text-base align-top ${columnClassNames[cellIndex] || ""} ${
                      col === "status" ? getStatusClass(row[col]) : "text-black"
                    }`}
                  >
                    {row[col]}
                  </td>
                ))}

                {onView && (
                  <td className={`py-4 px-6 align-top ${columnClassNames[columns.length] || ""}`}>
                    <ViewActionButton
                      onClick={() => onView(row)}
                      label={`View details for ${row?.name || row?.title || "record"}`}
                    />
                  </td>
                )}
              </tr>
            ))
          ) : null}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default DataTable;
